import { useState } from 'react';
import { toastService } from '../services/toastService';
import type { Source } from '../types';
import { sourcesService } from '../services/sourcesService';
import { useAuth } from './useAuth';
import { useLanguage } from '../contexts/LanguageContext';
import { Logger } from '../utils/logger';
// V4-FRONT-ANY-USAGE-BATCH-2 2026-05-28 — kill `error: any` via helper unifié
import { extractApiErrorMessage } from '../utils/apiError';

export function useSources() {
  const [sources, setSources] = useState<Source[]>([]);
  const { currentSpace } = useAuth();
  const { t } = useLanguage();

  const getSourceDisplayName = (source: Source): string => {
    return source.title || source.url || 'Source';
  };

  const addSource = async (sourceData: Omit<Source, 'id'>) => {
    if (!currentSpace) {
      toastService.error(t.toasts.noSpaceSelected);
      return;
    }
    // Optimistic update: add a temporary entry immediately so the list stays visible.
    // V4-SOURCES-BULK-ADD-DUPLICATE-RENDER — `crypto.randomUUID()` (pas `Date.now()`) :
    // un ajout groupé (SuggestSourcesModal) appelle `addSource()` en boucle synchrone,
    // sans attendre entre les itérations ; plusieurs appels dans le même tick recevaient
    // le même `Date.now()` → même tempId → chaque résolution serveur réécrivait TOUTES
    // les entrées partageant ce tempId, faisant converger la liste vers N doublons de la
    // dernière source résolue au lieu de N sources distinctes.
    const tempId = `temp-${crypto.randomUUID()}`;
    setSources((prev) => [...prev, { id: tempId, sync_status: 'pending', ...sourceData } as Source]);
    try {
      const data = await sourcesService.create(currentSpace.id, sourceData);
      
      if (data) {
        // Replace the temp entry with the real one from the backend
        setSources((prev) => prev.map((s) => (s.id === tempId ? data : s)));
        const sourceDisplayName = getSourceDisplayName(data);
        toastService.success(
          t.toasts.sourceAdded,
          t.toasts.sourceAddedDesc?.replace('{name}', sourceDisplayName) || `La source ${sourceDisplayName} est maintenant ajoutée.`
        );
      } else {
        setSources((prev) => prev.filter((s) => s.id !== tempId));
      }
    } catch (error: unknown) {
      setSources((prev) => prev.filter((s) => s.id !== tempId));
      Logger.error('Error adding source:', error);
      toastService.error(extractApiErrorMessage(error, t.toasts.sourceAddError));
    }
  };

  const updateSource = async (id: string, sourceData: Omit<Source, 'id'>) => {
    try {
      const data = await sourcesService.update(id, sourceData);

      if (data) {
        setSources((prev) =>
          prev.map((source) => (source.id === id ? data : source))
        );
        toastService.success(t.toasts.sourceUpdated, t.toasts.sourceUpdatedDesc || `Les paramètres de la source ont été sauvegardés.`);
      }
    } catch (error: unknown) {
      Logger.error('Error updating source:', error);
      toastService.error(extractApiErrorMessage(error, t.toasts.sourceUpdateError));
    }
  };

  const deleteSource = async (id: string) => {
    if (!currentSpace) {
      toastService.error(t.toasts.noSpaceSelected);
      return;
    }
    // TODO: Ajouter rollback de state sur deleteSource si l'API échoue
    // (actuellement la source disparaît de la liste sans retour arrière si l'API renvoie une erreur)
    try {
      const result = await sourcesService.delete(id, currentSpace.id);
      setSources((prev) => prev.filter((source) => source.id !== id));
      
      // Afficher un message approprié selon le résultat
      if (result.status === 'deleted') {
        toastService.success(t.toasts.sourceDeleted, t.toasts.sourceDeletedDesc || "La source a été retirée de votre espace.");
      } else {
        toastService.success(t.toasts.sourceUnlinked || t.toasts.sourceDeleted, t.toasts.sourceUnlinkedDesc || "La source a été dissociée.");
      }
    } catch (error: unknown) {
      Logger.error('Error deleting source:', error);
      toastService.error(extractApiErrorMessage(error, t.toasts.sourceDeleteError));
      // Re-synchroniser depuis l'API pour garantir la cohérence de l'UI
      try {
        const refreshed = await sourcesService.getAll(currentSpace.id);
        if (refreshed) setSources(refreshed);
      } catch {
        // Ignorer si le refresh échoue aussi
      }
    }
  };

  return {
    sources,
    setSources,
    addSource,
    updateSource,
    deleteSource
  };
}

